import { add, format, isValid, parse, parseISO, startOfDay } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import {
    AbsoluteDateTimeReferenceConditionModel,
    DateTimeReferenceConditionModel,
    RelativeDateTimeReferenceConditionModel
} from '../types/Segments';
import { RelativeDateConstraint } from '../types/Survey';

export type RelativeDateType = 'RelativeDate';

type DateSelectorModel = RelativeDateTimeReferenceConditionModel | AbsoluteDateTimeReferenceConditionModel;

export interface TimeZoneInfo {
    Id: string;
    IanaTZ?: string;
}

export type DateObjectOrString = RelativeDateConstraint | string | number | Date;
type SupportedOffsetUnit = 'year' | 'month' | 'week' | 'day' | 'hour' | 'minute';

type DurationByUnit = {
    years: number;
    months: number;
    weeks: number;
    days: number;
    hours: number;
    minutes: number;
};

const parseStrictDateTime = (dateString: unknown): Date | undefined => {
    if (typeof dateString !== 'string') {
        return undefined;
    }

    const parsed = parse(dateString, "yyyy-MM-dd'T'HH:mm:ss", new Date());
    if (!isValid(parsed)) {
        return undefined;
    }

    if (format(parsed, "yyyy-MM-dd'T'HH:mm:ss") !== dateString) {
        return undefined;
    }

    return parsed;
};

const isStartOfDay = (fromStartOfDay: RelativeDateConstraint['fromStartOfDay']): boolean => {
    return fromStartOfDay === true || fromStartOfDay === 'true';
};

const toDateSafe = (value: string | number | Date): Date | undefined => {
    if (value instanceof Date) {
        return isValid(value) ? value : undefined;
    }

    if (typeof value === 'string') {
        const parsedIso = parseISO(value);
        return isValid(parsedIso) ? parsedIso : undefined;
    }

    const parsedNumber = new Date(value);
    return isValid(parsedNumber) ? parsedNumber : undefined;
};

const normalizeOffsetUnit = (offsetUnits: string): SupportedOffsetUnit => {
    const normalized = offsetUnits.toLowerCase().endsWith('s') ? offsetUnits.toLowerCase().slice(0, -1) : offsetUnits.toLowerCase();
    if (normalized === 'year' || normalized === 'month' || normalized === 'week' || normalized === 'day' || normalized === 'hour' || normalized === 'minute') {
        return normalized;
    }

    return 'day';
};

const durationByUnit = (offsetUnit: SupportedOffsetUnit, offsetValue: number): DurationByUnit => {
    return {
        years: offsetUnit === 'year' ? offsetValue : 0,
        months: offsetUnit === 'month' ? offsetValue : 0,
        weeks: offsetUnit === 'week' ? offsetValue : 0,
        days: offsetUnit === 'day' ? offsetValue : 0,
        hours: offsetUnit === 'hour' ? offsetValue : 0,
        minutes: offsetUnit === 'minute' ? offsetValue : 0
    };
};

const applyFirstNonZeroOffset = (baseDate: Date, relativeDateObject: RelativeDateConstraint): Date => {
    if (relativeDateObject.offsetYears !== 0) {
        return add(baseDate, { years: relativeDateObject.offsetYears });
    }
    if (relativeDateObject.offsetMonths !== 0) {
        return add(baseDate, { months: relativeDateObject.offsetMonths });
    }
    if (relativeDateObject.offsetWeeks !== 0) {
        return add(baseDate, { weeks: relativeDateObject.offsetWeeks });
    }
    if (relativeDateObject.offsetDays !== 0) {
        return add(baseDate, { days: relativeDateObject.offsetDays });
    }
    if (relativeDateObject.offsetHours !== 0) {
        return add(baseDate, { hours: relativeDateObject.offsetHours });
    }
    if (relativeDateObject.offsetMinutes !== 0) {
        return add(baseDate, { minutes: relativeDateObject.offsetMinutes });
    }

    return baseDate;
};

const isValidIanaTimeZone = (timeZoneId: string): boolean => {
    try {
        Intl.DateTimeFormat('en-US', { timeZone: timeZoneId });
        return true;
    } catch {
        return false;
    }
};

const resolveIanaTimeZone = (timeZoneId?: string, timeZones?: TimeZoneInfo[]): string | undefined => {
    if (!timeZoneId) {
        return undefined;
    }

    const configuredIana = timeZones?.find(timeZone => timeZone.Id === timeZoneId)?.IanaTZ;
    if (configuredIana) {
        return configuredIana;
    }

    return isValidIanaTimeZone(timeZoneId) ? timeZoneId : undefined;
};

const startOfCurrentDayInTimeZone = (now: Date, ianaTimeZone: string): Date => {
    const zonedNow = toZonedTime(now, ianaTimeZone);
    const zonedStartOfDay = startOfDay(zonedNow);
    return fromZonedTime(zonedStartOfDay, ianaTimeZone);
};

const dateFromAbsoluteSelectorModel = (absoluteDateModel: AbsoluteDateTimeReferenceConditionModel, timeZones?: TimeZoneInfo[]): Date | undefined => {
    const parsedDateOnly = parse(absoluteDateModel.DateValue, 'yyyy-MM-dd', new Date());
    if (!isValid(parsedDateOnly)) {
        return undefined;
    }

    const hour24 = (absoluteDateModel.Hour % 12) + (absoluteDateModel.Period === 'PM' ? 12 : 0);
    const dateTimeFromFields = add(parsedDateOnly, { hours: hour24, minutes: absoluteDateModel.Minute });
    const ianaTimeZone = resolveIanaTimeZone(absoluteDateModel.TimeZoneId, timeZones);
    return ianaTimeZone ? fromZonedTime(dateTimeFromFields, ianaTimeZone) : dateTimeFromFields;
};

export const dateIsValid = (dateString: unknown): boolean => {
    return parseStrictDateTime(dateString) !== undefined;
};

export const relativeDateIsValid = (relativeDateObject?: Partial<RelativeDateConstraint> | null): relativeDateObject is RelativeDateConstraint => {
    return !!(relativeDateObject && relativeDateObject.type === 'RelativeDate');
};

const isRelativeDateObject = (value: DateObjectOrString): value is RelativeDateConstraint => {
    return typeof value === 'object' && value !== null && 'type' in value && value.type === 'RelativeDate';
};

const dateTimeFromRelativeDateObject = (relativeDateObject: RelativeDateConstraint): Date | undefined => {
    if (!relativeDateIsValid(relativeDateObject)) {
        return undefined;
    }

    const baseDate = isStartOfDay(relativeDateObject.fromStartOfDay) ? startOfDay(new Date()) : new Date();
    return applyFirstNonZeroOffset(baseDate, relativeDateObject);
};

const dateTimeFromRelativeDateSelectorModel = (relativeDateModel: RelativeDateTimeReferenceConditionModel, timeZones?: TimeZoneInfo[]): Date | undefined => {
    const offsetValue = relativeDateModel.ValueOffset * relativeDateModel.OffsetModifier;
    const offsetUnit = normalizeOffsetUnit(relativeDateModel.OffsetUnits);
    const duration = durationByUnit(offsetUnit, offsetValue);
    const ianaTimeZone = resolveIanaTimeZone(relativeDateModel.TimeZoneId, timeZones);
    const baseDate = relativeDateModel.FromStartOfDay
        ? (ianaTimeZone ? startOfCurrentDayInTimeZone(new Date(), ianaTimeZone) : startOfDay(new Date()))
        : new Date();

    return add(baseDate, duration);
};

export const dateTimeFromDateObjectOrString = (dateObjOrString?: DateObjectOrString | null): Date | undefined => {
    if (!dateObjOrString) {
        return undefined;
    }

    if (isRelativeDateObject(dateObjOrString)) {
        return dateTimeFromRelativeDateObject(dateObjOrString);
    }

    return toDateSafe(dateObjOrString);
};

export const dateTimeFromDateSelectorModel = (dateModel?: DateSelectorModel | null, timeZones?: TimeZoneInfo[]): Date | undefined => {
    if (!dateModel) {
        return undefined;
    }

    if (dateModel.Type === 'Relative' && 'ValueOffset' in dateModel && dateModel.ValueOffset !== undefined) {
        return dateTimeFromRelativeDateSelectorModel(dateModel, timeZones);
    }

    if (dateModel.Type === 'Absolute' && 'DateValue' in dateModel) {
        return dateFromAbsoluteSelectorModel(dateModel, timeZones);
    }

    return undefined;
};

export const dateTimeFromTimeSelectorModel = (timeModel: DateTimeReferenceConditionModel | AbsoluteDateTimeReferenceConditionModel): Date | undefined => {
    if (timeModel.Type === 'Absolute' && 'Hour' in timeModel && 'Minute' in timeModel && 'Period' in timeModel) {
        const parsed = parse(`${timeModel.Hour}:${timeModel.Minute} ${timeModel.Period}`, 'h:mm a', new Date());
        return isValid(parsed) ? parsed : undefined;
    }

    return undefined;
};

export const dateFromDateObjectOrString = (dateObjString?: DateObjectOrString | null): Date | undefined => {
    return dateTimeFromDateObjectOrString(dateObjString);
};
